import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Plus, Trash2 } from "lucide-react";
import { authApi, api, resolveImageUrl } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageFitControls from "@/components/admin/ImageFitControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptySlide = { image: "", kicker: "", title: "", copy: "", cta_label: "Shop Now", cta_href: "", image_fit: "cover", image_position: "center", overlay_mode: "text" };

export default function AdminCategoryBanners() {
  const [cats, setCats] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCat, setSelectedCat] = useState("");
  const [slides, setSlides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    Promise.all([
      api.get("/categories", { params: { group: "equipment" } }),
      api.get("/categories", { params: { group: "shop" } }),
      api.get("/site-settings"),
    ]).then(([eq, shop, s]) => {
      const combined = [
        ...eq.data.map((c) => ({ ...c, groupLabel: "Equipment" })),
        ...shop.data.map((c) => ({ ...c, groupLabel: "Pro Shop" })),
      ];
      setCats(combined);
      setSettings(s.data);
      if (combined.length) {
        const first = combined[0].slug;
        setSelectedCat(first);
        setSlides((s.data.category_banners || {})[first] || []);
      }
    });
  }, []);

  const pickCategory = (slug) => {
    setSelectedCat(slug);
    setSlides((settings?.category_banners || {})[slug] || []);
  };

  const setSlideField = (idx, k, v) => setSlides((arr) => arr.map((s, i) => (i === idx ? { ...s, [k]: v } : s)));
  const addSlide = () => setSlides((arr) => (arr.length >= 3 ? arr : [...arr, { ...emptySlide }]));
  const removeSlide = (idx) => setSlides((arr) => arr.filter((_, i) => i !== idx));

  const uploadSlideImage = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSlideField(idx, "image", resolveImageUrl(data.path));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingIdx(null); }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const nextBanners = { ...(settings.category_banners || {}), [selectedCat]: slides };
      const payload = { ...settings, category_banners: nextBanners };
      delete payload.key;
      await authApi.put("/admin/site-settings", payload);
      setSettings(payload);
      toast.success("Category banners saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const activeCat = cats.find((c) => c.slug === selectedCat);

  return (
    <div data-testid="admin-category-banners-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-2">Category Banners</h1>
          <p className="text-muted-foreground text-sm mt-1">Up to 3 rotating slides shown at the top of each category page (Ladies, Junior & all others).</p>
        </div>
        <button onClick={save} disabled={saving || !selectedCat} className="bg-[#E31837] text-white px-6 py-3 label-caps btn-transition hover:bg-[#b3122c] disabled:opacity-60" data-testid="save-category-banners-btn">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="bg-white border border-border p-5 mb-6 max-w-sm">
        <Label className="label-caps text-muted-foreground">Category</Label>
        <Select value={selectedCat} onValueChange={pickCategory}>
          <SelectTrigger className="mt-2 rounded-none" data-testid="category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
          <SelectContent className="rounded-none max-h-72">
            {cats.map((c) => (
              <SelectItem key={`${c.group}-${c.slug}`} value={c.slug} data-testid={`category-option-${c.slug}`}>
                {c.name} · {c.groupLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {activeCat && (
        <div className="space-y-5">
          {slides.length === 0 && (
            <div className="bg-white border border-border p-8 text-center text-muted-foreground text-sm">
              No custom slides for <b>{activeCat.name}</b> yet — the page shows default slides. Add one below to customize.
            </div>
          )}

          {slides.map((s, idx) => (
            <div key={idx} className="bg-white border border-border p-5" data-testid={`slide-editor-${idx}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="label-caps">Slide {idx + 1}</h3>
                <button onClick={() => removeSlide(idx)} className="text-muted-foreground hover:text-[#E31837] btn-transition flex items-center gap-1 text-xs" data-testid={`remove-slide-${idx}`}>
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              </div>
              <div className="grid md:grid-cols-[220px_1fr] gap-5">
                <div>
                  <div className="h-28 bg-secondary border border-border overflow-hidden flex items-center justify-center">
                    {s.image ? <img src={s.image} alt="" className="w-full h-full" style={{ objectFit: s.image_fit || "cover", objectPosition: s.image_position || "center" }} /> : <span className="text-muted-foreground text-xs">No image</span>}
                  </div>
                  <input ref={(el) => (fileRefs.current[idx] = el)} type="file" accept="image/*" hidden onChange={(e) => uploadSlideImage(idx, e.target.files[0])} data-testid={`slide-image-input-${idx}`} />
                  <button type="button" onClick={() => fileRefs.current[idx]?.click()} disabled={uploadingIdx === idx} className="w-full mt-2 border border-[#0A0A0A] py-2 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid={`slide-upload-${idx}`}>
                    {uploadingIdx === idx ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Image</>}
                  </button>
                  <div className="mt-2">
                    <ImageFitControls
                      fit={s.image_fit}
                      position={s.image_position}
                      onChange={(patch) => setSlides((arr) => arr.map((sl, i) => (i === idx ? { ...sl, ...patch } : sl)))}
                      testidPrefix={`slide-${idx}-image`}
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="label-caps text-muted-foreground">Overlay Mode</Label>
                    <div className="mt-1 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSlideField(idx, "overlay_mode", "image_only")}
                        className={`flex-1 border px-3 py-2 text-xs font-semibold uppercase tracking-wide btn-transition ${(s.overlay_mode || "text") === "image_only" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                        data-testid={`slide-overlay-imageonly-${idx}`}
                      >
                        Image Only
                      </button>
                      <button
                        type="button"
                        onClick={() => setSlideField(idx, "overlay_mode", "text")}
                        className={`flex-1 border px-3 py-2 text-xs font-semibold uppercase tracking-wide btn-transition ${(s.overlay_mode || "text") === "text" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                        data-testid={`slide-overlay-text-${idx}`}
                      >
                        With Text
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Choose "Image Only" if your uploaded image already has text/buttons baked in.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-caps text-muted-foreground">Kicker</Label>
                      <Input value={s.kicker} onChange={(e) => setSlideField(idx, "kicker", e.target.value)} className="mt-1 rounded-none" placeholder="New Season" data-testid={`slide-kicker-${idx}`} disabled={(s.overlay_mode || "text") === "image_only"} />
                    </div>
                    <div>
                      <Label className="label-caps text-muted-foreground">Title</Label>
                      <Input value={s.title} onChange={(e) => setSlideField(idx, "title", e.target.value)} className="mt-1 rounded-none" placeholder={activeCat.name} data-testid={`slide-title-${idx}`} disabled={(s.overlay_mode || "text") === "image_only"} />
                    </div>
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Copy</Label>
                    <Textarea rows={2} value={s.copy} onChange={(e) => setSlideField(idx, "copy", e.target.value)} className="mt-1 rounded-none" data-testid={`slide-copy-${idx}`} disabled={(s.overlay_mode || "text") === "image_only"} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-caps text-muted-foreground">Button Label</Label>
                      <Input value={s.cta_label} onChange={(e) => setSlideField(idx, "cta_label", e.target.value)} className="mt-1 rounded-none" placeholder="Shop Now" data-testid={`slide-cta-label-${idx}`} disabled={(s.overlay_mode || "text") === "image_only"} />
                    </div>
                    <div>
                      <Label className="label-caps text-muted-foreground">Button Link</Label>
                      <Input value={s.cta_href} onChange={(e) => setSlideField(idx, "cta_href", e.target.value)} className="mt-1 rounded-none" placeholder="/shop" data-testid={`slide-cta-href-${idx}`} disabled={(s.overlay_mode || "text") === "image_only"} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {slides.length < 3 && (
            <button onClick={addSlide} className="w-full border border-dashed border-border py-4 label-caps text-muted-foreground hover:border-[#E31837] hover:text-[#E31837] btn-transition flex items-center justify-center gap-2" data-testid="add-slide-btn">
              <Plus className="h-4 w-4" /> Add Slide ({slides.length}/3)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
